import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { getZendeskMessagingToken } from "@src/appV2/Zendesk/useZendeskSsoToken";
import { useEffect, useState } from "react";
import Smooch from "smooch";
import { z } from "zod";

interface UseSmoochChatParams {
  workerId?: string;
}

const chatMessageSchema = z
  .object({
    id: z.string(),
    role: z.enum(["user", "business"]),
    displayName: z.string(),
    text: z.string(),
    avatarUrl: z.string().optional(),
    received: z.number().optional(),
  })
  .passthrough();

export type ChatMessage = z.infer<typeof chatMessageSchema>;

const smoochFileMessageSchema = z.object({
  altText: z.string(),
  attachmentId: z.string(),
  id: z.string(),
  mediaType: z.string(),
  mediaUrl: z.string(),
  type: z.enum(["file", "image"]),
});

export type SmoochFileMessage = z.infer<typeof smoochFileMessageSchema>;

export function parseSmoochFileMessage(message: unknown): SmoochFileMessage | undefined {
  const result = smoochFileMessageSchema.safeParse(message);
  return result.success ? result.data : undefined;
}

interface UseSmoochChatResult {
  conversationId: string | undefined;
  isReady: boolean;
  messages: ChatMessage[];
}

async function initializeSmooch(params: UseSmoochChatParams): Promise<void> {
  const { workerId } = params;

  const ssoTokenResult = await getZendeskMessagingToken();
  if (!isDefined(ssoTokenResult)) {
    throw new Error("Failed to get Zendesk SSO token");
  }

  // Allow Render to be called before this is awaited.
  // eslint-disable-next-line @typescript-eslint/return-await
  return Smooch.init({
    // @ts-expect-error: configBaseUrl is not in @types/smooch
    configBaseUrl: environmentConfig.REACT_APP_ZENDESK_BASE_URL,
    embedded: true,
    externalId: String(workerId),
    integrationId: environmentConfig.REACT_APP_SUNSHINE_WEB_INTEGRATION_ID,
    jwt: ssoTokenResult,
  });
}

export async function createSupportConversation(): Promise<string> {
  const conversation = await Smooch.createConversation({
    displayName: "Support Conversation",
    description: "A support chat has been started.",
  });

  if (!isDefined(conversation) || !isDefined(conversation.id)) {
    throw new Error("Failed to create conversation");
  }

  return conversation.id;
}

// This seems required for embedded mode to work for smooch
export function renderSmoochHiddenContainer(): void {
  let hidden = document.querySelector<HTMLDivElement>("#smooch-hidden-container");
  if (!hidden) {
    hidden = document.createElement("div");
    hidden.id = "smooch-hidden-container";
    hidden.style.cssText = "width:0;height:0;overflow:hidden";
    document.body.append(hidden);
  }

  Smooch.render(hidden);
}

export function useNewSmoochChat(params: UseSmoochChatParams): UseSmoochChatResult {
  const { workerId } = params;
  const [messages, setMessages] = useState<Record<string, ChatMessage>>({});
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const logMetadata = { workerId };
    let cancelled = false;

    const initialize = async (): Promise<void> => {
      if (!workerId) {
        return;
      }

      try {
        Smooch.on("ready", () => {
          logEvent(APP_V2_APP_EVENTS.SMOOCH_CHAT_READY, logMetadata);
        });

        Smooch.on("conversation:added", (_participants, data) => {
          logEvent(APP_V2_APP_EVENTS.SMOOCH_CONVERSATION_ADDED, {
            result: data,
            ...logMetadata,
          });
        });

        Smooch.on("connected", (data) => {
          logEvent(APP_V2_APP_EVENTS.SMOOCH_CHAT_CONNECTED, {
            result: data,
            ...logMetadata,
          });
        });

        Smooch.on("message", (message: unknown) => {
          const parseResult = chatMessageSchema.safeParse(message);
          if (!parseResult.success) {
            const fallbackMessage = message as { id?: string };
            const { id } = fallbackMessage;
            if (isDefined(id)) {
              setMessages((previous) => ({
                ...previous,
                [id]: message as ChatMessage,
              }));
            }

            return;
          }

          setMessages((previous) => ({
            ...previous,
            [parseResult.data.id]: parseResult.data,
          }));
        });

        const initializationPromise = initializeSmooch({ workerId });
        renderSmoochHiddenContainer();
        await initializationPromise;
        setIsReady(true);

        const conversation = await createSupportConversation();
        if (!cancelled) {
          setConversationId(conversation);
        }
      } catch (error) {
        logError("Error initializing Smooch chat", {
          metadata: { workerId },
          error,
        });
      }
    };

    void initialize();

    return () => {
      cancelled = true;
      void Smooch.destroy();
    };
  }, [workerId]);

  return { conversationId, isReady, messages: Object.values(messages) };
}
